import React from 'react';
import Select, {
	components,
	type ControlProps,
	type SingleValueProps,
} from 'react-select';
import {useTranslation} from 'react-i18next';

export interface BaseOption {
	label: string;
	value: string | number;
	icon?: string;
	[key: string]: unknown; // любые доп. поля
}

type Props = {
	value: BaseOption | null;
	setValue: (value: BaseOption | null) => void;
	options?: BaseOption[];
	fontSize?: number;
	height?: number;
	placeholder?: string;
};

// Контрол без иконок — только разметка
const CustomControl = (props: ControlProps<BaseOption, false>) => (
	<components.Control {...props}>{props.children}</components.Control>
);

// Иконка слева от текста + поворот при открытии
const CustomSingleValue = (props: SingleValueProps<BaseOption, false>) => {
	/*const {menuIsOpen} = props.selectProps;*/

	return (
		<components.SingleValue {...props}>
			<span style={{display: 'flex', alignItems: 'center'}}>
				<span style={{display: 'flex', alignItems: 'center', gap: 8}}>
					<>
						{props.data?.icon && <img src={props?.data?.icon} alt="icon" />}
						{props.children}
					</>
				</span>
			</span>
		</components.SingleValue>
	);
};

const ReactSelectStyled: React.FC<Props> = ({
	value,
	setValue,
	options,
	fontSize,
	height,
	placeholder,
}) => {
	const {t} = useTranslation();
	return (
		<Select
			value={value}
			onChange={(newValue) => {
				setValue(newValue ?? {label: '', value: ''});
			}}
			placeholder={t(placeholder)}
			options={options}
			isSearchable={false}
			components={{
				IndicatorSeparator: () => null,
				DropdownIndicator: () => null,
				Control: CustomControl,
				SingleValue: CustomSingleValue,
			}}
			// вырубаем фирменную синеву на уровне темы
			theme={(t) => ({
				...t,
				colors: {
					...t.colors,
					primary: 'transparent',
					primary25: 'transparent',
					primary50: 'transparent',
					primary75: 'transparent',
					neutral20: 'transparent',
					neutral30: 'transparent',
					neutral80: 'var(--color-lightBrown)',
				},
			})}
			styles={{
				control: (base, state) => ({
					...base,
					width: '100%',
					height: height ? `${height}px` : '45px',
					/*borderRadius: '24px',*/
					backgroundColor: state.menuIsOpen
						? 'var(--color-lightBrown)'
						: 'var(--color-light)',
					color: state.menuIsOpen
						? 'var(--color-light)'
						: 'var(--color-lightBrown)',
					border: '1px solid #c4c4c4',
					boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.07)',
					':hover': {borderColor: 'transparent'},
				}),
				valueContainer: (base) => ({
					...base,
					width: '100%',
					display: 'flex',
					justifyContent: 'center', // центрируем значение (иконка+текст)
				}),
				singleValue: (base, state) => ({
					...base,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: state.selectProps.menuIsOpen
						? 'var(--color-light)'
						: 'var(--color-lightBrown)',
					fontSize: fontSize ? `${fontSize}px` : '20px',
					lineHeight: '20px',
				}),
				menu: (base) => ({
					...base,
					width: '100%',
					/*borderRadius: '24px',*/
				}),
				menuList: (base) => ({
					...base,
					display: 'flex',
					flexDirection: 'column',
					padding: 0, // убираем отступы
					/*borderRadius: '24px',*/
					/*border: '2px solid #9D4A38',*/
					background: 'var(--color-light)',
				}),

				option: (base) => ({
					...base,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '12px 16px', // вернули паддинг на саму опцию
					color: 'var(--color-lightBrown)',
					fontSize: fontSize ? `${fontSize}px` : '20px',
					fontWeight: 400,
					backgroundColor: 'transparent',
					cursor: 'pointer',
					':hover': {
						backgroundColor: 'var(--color-lightBrown)',
						color: 'var(--color-light)',
					},
					// сглаживаем углы для первой и последней опции
					'&:first-of-type': {
						borderTopLeftRadius: '22px',
						borderTopRightRadius: '22px',
					},
					'&:last-of-type': {
						borderBottomLeftRadius: '22px',
						borderBottomRightRadius: '22px',
					},
				}),
				indicatorsContainer: (base) => ({...base, display: 'none'}),
			}}
		/>
	);
};

export default ReactSelectStyled;
